// Umami Analytics Service
// Serviço para integração com Umami Analytics

class UmamiAnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.websiteId = null;
    this.init();
  }

  // Inicializar o serviço
  init() {
    if (typeof window !== 'undefined') {
      // Verificar se o script do Umami foi carregado
      const umamiScript = document.querySelector('script[data-website-id]');
      if (umamiScript) {
        this.websiteId = umamiScript.getAttribute('data-website-id');
        this.isInitialized = true;
      }
      
      // Aguardar o carregamento do umami
      this.waitForUmami();
    }
  }

  // Aguardar o carregamento do objeto umami
  waitForUmami() {
    const checkUmami = () => {
      if (typeof window !== 'undefined' && window.umami) {
        this.isInitialized = true;
        console.log('Umami Analytics inicializado com sucesso');
      } else {
        setTimeout(checkUmami, 100);
      }
    };
    checkUmami();
  }

  // Verificar se o Umami está disponível
  isAvailable() {
    return this.isInitialized && typeof window !== 'undefined' && window.umami;
  }

  // Rastrear evento personalizado
  trackEvent(eventName, eventData = {}) {
    if (!this.isAvailable()) {
      console.warn('Umami Analytics não está disponível');
      return;
    }

    try {
      window.umami.track(eventName, eventData);
    } catch (error) {
      console.error('Erro ao rastrear evento no Umami:', error);
    }
  }

  // Rastrear visualização de página (automático, mas pode ser usado manualmente)
  trackPageView(url = window.location.pathname, title = document.title) {
    if (!this.isAvailable()) return;

    try {
      window.umami.track('pageview', {
        url: url,
        title: title
      });
    } catch (error) {
      console.error('Erro ao rastrear página no Umami:', error);
    }
  }

  // Rastrear login
  trackLogin(method = 'email') {
    this.trackEvent('login', {
      method: method,
      category: 'authentication'
    });
  }

  // Rastrear logout
  trackLogout() {
    this.trackEvent('logout', {
      category: 'authentication'
    });
  }

  // Rastrear clique em botão
  trackButtonClick(buttonName, section = '') {
    this.trackEvent('button_click', {
      button_name: buttonName,
      section: section,
      category: 'interaction'
    });
  }

  // Rastrear navegação no menu
  trackNavigation(destination) {
    this.trackEvent('navigation', {
      destination: destination,
      category: 'navigation'
    });
  }

  // Rastrear interação com WhatsApp
  trackWhatsAppInteraction(action, instanceName = '') {
    this.trackEvent('whatsapp_interaction', {
      action: action,
      instance_name: instanceName,
      category: 'whatsapp'
    });
  }

  // Rastrear uso do CRM
  trackCRMAction(action, contactId = '') {
    this.trackEvent('crm_action', {
      action: action,
      contact_id: contactId,
      category: 'crm'
    });
  }

  // Rastrear visualização de dashboard
  trackDashboardView(dashboardType) {
    this.trackEvent('dashboard_view', {
      dashboard_type: dashboardType,
      category: 'dashboard'
    });
  }

  // Rastrear erro
  trackError(errorType, errorMessage = '') {
    this.trackEvent('error', {
      error_type: errorType,
      error_message: errorMessage,
      category: 'error'
    });
  }

  // Rastrear tempo de sessão
  trackSessionDuration(duration) {
    this.trackEvent('session_duration', {
      duration_seconds: Math.round(duration / 1000),
      category: 'engagement'
    });
  }

  // Rastrear conversão/meta
  trackConversion(conversionName, value = 0) {
    this.trackEvent('conversion', {
      conversion_name: conversionName,
      value: value,
      category: 'conversion'
    });
  }

  // Rastrear download de arquivo
  trackDownload(fileName, fileType = '') {
    this.trackEvent('file_download', {
      file_name: fileName,
      file_type: fileType,
      category: 'download'
    });
  }

  // Rastrear busca
  trackSearch(searchTerm, resultsCount = 0) {
    this.trackEvent('search', {
      search_term: searchTerm,
      results_count: resultsCount,
      category: 'search'
    });
  }

  // Definir propriedades customizadas do usuário
  setUserProperties(properties) {
    if (!this.isAvailable()) return;

    try {
      // Umami não tem setUserProperties nativo, mas podemos rastrear como evento
      this.trackEvent('user_properties', {
        ...properties,
        category: 'user'
      });
    } catch (error) {
      console.error('Erro ao definir propriedades do usuário no Umami:', error);
    }
  }

  // Definir ID do usuário
  setUserId(userId) {
    if (!this.isAvailable()) return;

    try {
      // Umami não tem setUserId nativo, mas podemos rastrear como evento
      this.trackEvent('user_identification', {
        user_id: userId,
        category: 'user'
      });
    } catch (error) {
      console.error('Erro ao definir ID do usuário no Umami:', error);
    }
  }

  // Rastrear timing personalizado
  trackTiming(category, variable, value, label = '') {
    this.trackEvent('timing', {
      category: category,
      variable: variable,
      value: value,
      label: label
    });
  }

  // Obter informações do website
  getWebsiteId() {
    return this.websiteId;
  }

  // Verificar status da configuração
  getStatus() {
    return {
      initialized: this.isInitialized,
      available: this.isAvailable(),
      websiteId: this.websiteId
    };
  }
}

// Criar instância única do serviço
const umamiAnalyticsService = new UmamiAnalyticsService();

export default umamiAnalyticsService;

// Exportar também métodos individuais para facilitar o uso
export const {
  trackEvent,
  trackPageView,
  trackLogin,
  trackLogout,
  trackButtonClick,
  trackNavigation,
  trackWhatsAppInteraction,
  trackCRMAction,
  trackDashboardView,
  trackError,
  trackSessionDuration,
  trackConversion,
  trackDownload,
  trackSearch,
  setUserProperties,
  setUserId,
  trackTiming,
  getWebsiteId,
  getStatus
} = umamiAnalyticsService;